import * as T from "../utilities/frontendTypes";
import { queryKeys } from "../utilities/queryKeys";
import { useMutationWithUI } from "../utilities/useMutationWithUI";
import {
  serviceProviderSubmitSow,
  serviceProviderSaveProgressSow,
  newRetainerInvoice,
  newFinalInvoice,
  finalInvoiceSubmit,
  resubmitInvoice,
  updateFirmProfile,
  addPractitioner,
  updatePractitioner,
  deletePractitioner,
  type ResubmitRetainerPayload,
  type ResubmitFinalPayload,
} from "./serviceProvider";
import textData from "../textData.json";

export function useSubmitSow() {
  return useMutationWithUI({
    // Cross-role: submitting a SoW changes sowStatus, which the admin, founder,
    // and expert dashboards all read — invalidate every dashboard (Rule 2), not
    // just the SP's own.
    mutationFn: (sowDetails: T.SowDetailToBackend) => serviceProviderSubmitSow(sowDetails),
    invalidateKeys: [queryKeys.sows.all, queryKeys.dashboards.all],
    successMessage: textData.Toasts.Provider.SowSubmitted,
  });
}

export function useSaveProgressSow() {
  return useMutationWithUI({
    // Cross-role: saving progress flips sowStatus from "New SoW Request" to
    // "SoW In Progress" — a change visible on the SP, admin, and founder
    // dashboards — so invalidate every dashboard (Rule 2). Without this the
    // dashboards keep showing the stale "New SoW Request" status.
    mutationFn: (sowDetails: T.SowDetailToBackend) => serviceProviderSaveProgressSow(sowDetails),
    invalidateKeys: [queryKeys.sows.all, queryKeys.dashboards.all],
    successMessage: textData.Toasts.Provider.SowProgressSaved,
    showLoadingOverlay: false,
  });
}

export function useNewRetainerInvoice() {
  return useMutationWithUI({
    mutationFn: (args: { sowId: string; totalArray: number[]; attestations: { accurateInfo: boolean }; memo?: string }) =>
      newRetainerInvoice(args.sowId, args.totalArray, args.attestations, args.memo),
    invalidateKeys: [queryKeys.sows.all, queryKeys.invoices.all, queryKeys.dashboards.all],
    successMessage: textData.Toasts.Provider.RetainerInvoiceCreated,
  });
}

export function useNewFinalInvoice() {
  return useMutationWithUI({
    mutationFn: (args: { sowId: string; sowItems: T.SowItem[]; preTax: number; tax: number; existingRetainerInvoiceTotal: number; memo?: string }) =>
      newFinalInvoice(args.sowId, args.sowItems, args.preTax, args.tax, args.existingRetainerInvoiceTotal, args.memo),
    invalidateKeys: [queryKeys.sows.all, queryKeys.invoices.all, queryKeys.dashboards.all],
    successMessage: textData.Toasts.Provider.FinalInvoiceCreated,
  });
}

export function useFinalInvoiceSubmit() {
  return useMutationWithUI({
    mutationFn: (args: { invoiceId: string; attestations: { accurateInfo: boolean; deliverablesConfirmed: boolean } }) =>
      finalInvoiceSubmit(args.invoiceId, args.attestations),
    invalidateKeys: [queryKeys.sows.all, queryKeys.invoices.all, queryKeys.dashboards.all],
    successMessage: textData.Toasts.Provider.InvoiceSubmitted,
  });
}

export function useResubmitInvoice() {
  return useMutationWithUI({
    mutationFn: (args: { invoiceId: string; payload: ResubmitRetainerPayload | ResubmitFinalPayload }) =>
      resubmitInvoice(args.invoiceId, args.payload),
    invalidateKeys: [queryKeys.sows.all, queryKeys.invoices.all, queryKeys.dashboards.all],
    successMessage: textData.Toasts.Provider.InvoiceResubmitted,
  });
}

export function useUpdateFirmProfile() {
  return useMutationWithUI({
    // Cross-role: an SP editing their firm (e.g. tax numbers) changes data the admin
    // Firm Details view and dashboards also read, so invalidate the whole providers
    // namespace + every dashboard, not just the SP's own firmProfile (Rule 2 — R4).
    mutationFn: (firmData: Record<string, unknown>) => updateFirmProfile(firmData),
    invalidateKeys: [queryKeys.providers.all, queryKeys.dashboards.all],
    successMessage: textData.Toasts.Provider.FirmProfileUpdated,
  });
}

export function useAddPractitioner() {
  return useMutationWithUI({
    mutationFn: (data: { name: string; email: string }) => addPractitioner(data),
    invalidateKeys: [queryKeys.providers.practitioners()],
    successMessage: textData.Toasts.Provider.PractitionerAdded,
  });
}

export function useUpdatePractitioner() {
  return useMutationWithUI({
    mutationFn: (args: { practitionerId: string; data: { name: string; email: string } }) =>
      updatePractitioner(args.practitionerId, args.data),
    invalidateKeys: [queryKeys.providers.practitioners()],
    successMessage: textData.Toasts.Provider.PractitionerUpdated,
  });
}

export function useDeletePractitioner() {
  return useMutationWithUI({
    mutationFn: (practitionerId: string) => deletePractitioner(practitionerId),
    invalidateKeys: [queryKeys.providers.practitioners()],
    successMessage: textData.Toasts.Provider.PractitionerRemoved,
  });
}
